import { BehaviorSubject } from "rxjs";

/**
 * Configuration payload for opening the text viewer modal.
 */
export interface TextViewerConfig {
  /** Text rendered in the viewer. */
  text: string;
  /** Monaco language id used for highlighting. */
  language: string;
  /** Optional modal title shown in the text viewer header. */
  title?: string;
  /** the value is NULL (text is then ""); Apply with no text stores an empty string */
  isNull?: boolean;
  /**
   * Given, the viewer is an editor: the text can be changed and Apply hands the new
   * text here and closes. Absent, the viewer is read-only.
   */
  onApply?: (text: string) => void;
  /** Given (an editor on a column that accepts NULL), the viewer offers "Set to NULL". */
  onSetNull?: () => void;
}

/**
 * Global stream that controls the text viewer modal state.
 *
 * - `null` closes the viewer
 * - `TextViewerConfig` opens/updates the viewer
 */
export const textViewerSubject = new BehaviorSubject<TextViewerConfig | null>(
  null,
);

/**
 * Opens the text viewer modal with the provided configuration.
 *
 * @param config Configuration for the viewer content and title.
 */
export const showTextViewer = (config: TextViewerConfig) => {
  textViewerSubject.next(config);
};

/**
 * Closes the text viewer modal.
 */
export const closeTextViewer = () => {
  textViewerSubject.next(null);
};

/**
 * The model's UI metadata for a column: table_fields.metadata, declared in zoo as
 * @ui(widget=code, language=typescript) and carried by the firewall on every
 * result column (source_table / source_column / metadata) and catalog column.
 */
export interface ColumnUi {
  widget?: string;
  language?: string;
  [key: string]: unknown;
}

/** Parses a column's metadata as the firewall sends it (a JSON object text); undefined when none. */
export function parseColumnUi(raw: unknown): ColumnUi | undefined {
  if (raw == null || raw === "") return undefined;
  if (typeof raw === "object") return raw as ColumnUi;
  if (typeof raw !== "string") return undefined;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as ColumnUi) : undefined;
  } catch {
    return undefined;
  }
}

/** True when the model says the column holds code: every value opens in the viewer. */
export function isCodeColumn(ui: ColumnUi | undefined): boolean {
  return ui?.widget === "code";
}

/** Longest single-line value a grid cell shows in full. */
export const INLINE_TEXT_MAX_LENGTH = 80;

/**
 * True when a cell value needs the viewer: any multi-line string, or a single
 * line longer than a grid cell shows.
 */
export function needsTextViewer(value: unknown): value is string {
  return (
    typeof value === "string" &&
    (value.length > INLINE_TEXT_MAX_LENGTH || value.includes("\n"))
  );
}

const LANGUAGE_BY_SUFFIX: Record<string, string> = {
  ts: "typescript",
  tsx: "typescript",
  js: "javascript",
  jsx: "javascript",
  sql: "sql",
  json: "json",
  yaml: "yaml",
  yml: "yaml",
  html: "html",
  xml: "xml",
  css: "css",
  md: "markdown",
  py: "python",
  go: "go",
  sh: "shell",
};

/**
 * Returns the Monaco language id for a column: the model's `language` when the
 * column carries UI metadata, else read from the column name's last
 * underscore-separated word (`source_ts` is typescript, `compiled_js` is
 * javascript). A column with neither is plaintext.
 */
export function languageForColumn(columnName: string, ui?: ColumnUi): string {
  if (typeof ui?.language === "string" && ui.language !== "") return ui.language;
  const suffix = columnName.toLowerCase().split("_").pop() ?? "";
  return LANGUAGE_BY_SUFFIX[suffix] ?? "plaintext";
}
